import React, { useState } from 'react'
import { RefreshCw01, HelpCircle } from '@untitled-ui/icons-react'
import { 
  MyConfirmModalWithChildren, 
  MyTextArea, 
  MyCheckbox, 
  MyChip, 
  MyButton 
} from '@interstellar-component'

export default function RejectResendForm({ open, onClose, onConfirm, zIndex, loading, ektpByHr = false, kycEnabled = true }) {
  const [notes, setNotes] = useState('')
  const [reasons, setReasons] = useState([])
  const [notesError, setNotesError] = useState('')

  const handleReasonToggle = (reason, checked) => {
    if (checked) {
      setReasons([...reasons, reason])
    } else {
      setReasons(reasons.filter((r) => r !== reason))
    }
  }

  // Notes are optional, but the BE validator requires 3-2000 characters when a
  // value is sent. Catch a too-short note (e.g. a lone "-") inline instead of
  // letting it through to a 422.
  const handleConfirm = () => {
    const trimmed = notes.trim()
    if (trimmed && trimmed.length < 3) {
      setNotesError('Notes must be 3-2000 characters')
      return
    }
    setNotesError('')
    // Defense-in-depth: never submit a reason whose checkbox is hidden (the BE
    // rejects it too). ktp_unclear doesn't apply on an hr-ektp flow (HR owns the
    // KTP); face_verify_failed doesn't apply when KYC is off (no face check).
    let safeReasons = reasons
    if (ektpByHr) safeReasons = safeReasons.filter((r) => r !== 'ktp_unclear')
    if (!kycEnabled) safeReasons = safeReasons.filter((r) => r !== 'face_verify_failed')
    onConfirm?.({ reasons: safeReasons, notes })
  }

  const footerActions = (
    <div className="flex w-full gap-3 mt-6 mb-6">
      <MyButton
        expanded
        color="secondary"
        variant="outlined"
        size="lg"
        onClick={onClose}
        disabled={loading}
      >
        Cancel
      </MyButton>
      <MyButton
        expanded
        color="secondary"
        variant="outlined"
        size="lg"
        onClick={handleConfirm}
        disabled={loading}
        customClassname="!border-brand/300 !text-brand/700 hover:!bg-brand/50"
      >
        {loading ? 'Submitting...' : 'Reject & Resend Form'}
      </MyButton>
    </div>
  )

  return (
    <MyConfirmModalWithChildren
      open={open}
      onClose={onClose}
      zIndex={zIndex}
      forceBlur
      title="Reject Verification & Resend Form"
      message="Select the reason for rejection and add a note so the employee can correct the data and resubmit the form."
      icon={<RefreshCw01 className="text-brand/900" />}
      bgColor="bg-white"
      disableBgPattern
      enableDoubleRing
      footerClassName="!px-0 !mt-0 !mb-0"
      positiveActionWord={footerActions}
    >
      <div className="flex flex-col gap-6">
        {/* Rejection Notes */}
        <div className="flex flex-col gap-1.5">
          <MyTextArea
            label={
              <div className="flex items-center gap-1 font-semibold text-gray-700">
                Rejection Notes
                <HelpCircle size={16} className="text-gray-400" />
              </div>
            }
            placeholder="Enter a description... (optional)"
            helperText={
              notesError
                ? undefined
                : 'Optionally explain the reason for rejection and the corrections required.'
            }
            errors={notesError ? { rejectionNotes: notesError } : undefined}
            value={notes}
            onChangeInput={(e) => {
              setNotes(e.target.value)
              if (notesError) setNotesError('')
            }}
            name="rejectionNotes"
          />
          {notesError && (
            <p className="text-sm-regular text-error/600">{notesError}</p>
          )}
        </div>

        {/* Reasons List */}
        <div className="flex flex-col gap-3">
          {/* "E-KTP tidak jelas" only applies when the CANDIDATE uploaded the
              e-KTP. On an ektp='hr' flow HR owns that image and fixes it at
              source, so the reason is hidden (and the BE refuses it). */}
          {!ektpByHr && (
            <div className="flex items-center gap-3">
              <MyCheckbox
                name="reason1"
                checked={reasons.includes('ktp_unclear')}
                onChangeForm={(e) => handleReasonToggle('ktp_unclear', e.target.checked)}
              />
              <MyChip
                label="E-KTP photo is unclear or cropped"
                color="gray"
                variant="filled"
                size="md"
                rounded="full"
              />
            </div>
          )}
          {/* "Verifikasi wajah gagal" only applies when the flow includes a face
              check. With KYC off (no selfie/portrait) it's hidden — and the BE
              rejects it — so HR can't reject for a check that never ran. */}
          {kycEnabled && (
            <div className="flex items-center gap-3">
              <MyCheckbox
                name="reason2"
                checked={reasons.includes('face_verify_failed')}
                onChangeForm={(e) => handleReasonToggle('face_verify_failed', e.target.checked)}
              />
              <MyChip
                label="Face verification failed"
                color="gray"
                variant="filled"
                size="md"
                rounded="full"
              />
            </div>
          )}
        </div>
      </div>
    </MyConfirmModalWithChildren>
  )
}
